import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageHeader, Modal, Toast } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Select from "@/components/Select";
import MultiSelect from "@/components/MultiSelect";
import CoverageHint from "@/components/CoverageHint";

/* Parametrii > Acces (SPEC-acces-profiluri.md): profilurile de acces pe coloane × secțiunile platformei pe rânduri.
   Profilurile de sistem (câte unul pe rol) le modifică Adminul; profilurile personalizate se alocă persoanelor, pe o
   structură și/sau pe categorii de personal, opțional pe o perioadă. Dependențele unei chei se bifează automat. */

const LEVELS = ["view", "operate", "admin"];
const LEVEL_STYLE = {
  none: { bg: "transparent", color: "#94A3B8" },
  view: { bg: "var(--tint-50)", color: "var(--blue-primary)" },
  operate: { bg: "var(--tint-100)", color: "var(--navy-3)" },
  admin: { bg: "var(--navy-3)", color: "#FFFFFF" },
  custom: { bg: "#FEF3C7", color: "#92400E" },
  auto: { bg: "#F1F5F9", color: "#475569" },
};
const ROLE_LABELS = { admin: "Admin", hr: "HR", manager: "Manager", employee: "Salariat" };

const closeKeys = (keys, reg) => {
  const out = new Set(); const stack = [...keys];
  while (stack.length) { const k = stack.pop(); if (out.has(k)) continue; out.add(k); stack.push(...((reg.keys[k] || {}).deps || [])); }
  return out;
};
const levelKeys = (sec, level) => {
  const out = [];
  for (const lv of LEVELS) { out.push(...(sec.levels[lv] || [])); if (lv === level) return out; }
  return level === "none" ? [] : out;
};
const sectionLevel = (sec, keys) => {
  const own = LEVELS.flatMap(lv => sec.levels[lv] || []);
  const present = own.filter(k => keys.has(k));
  let best = "none";
  for (const lv of LEVELS) if (sec.levels[lv] && levelKeys(sec, lv).every(k => keys.has(k))) best = lv;
  const covered = new Set(best === "none" ? [] : levelKeys(sec, best));
  return present.some(k => !covered.has(k)) ? "custom" : best;
};
// Secțiunile alese efectiv (fără cele intrate doar ca dependență a altei secțiuni) — ca core/access.py::chosen_keys.
const chosenSections = (keys, reg) => {
  const cross = new Set();
  keys.forEach(k => ((reg.keys[k] || {}).deps || []).forEach(d => { if (reg.keys[d] && reg.keys[d].section !== reg.keys[k].section) cross.add(d); }));
  return new Set([...keys].filter(k => !cross.has(k) && reg.keys[k]).map(k => reg.keys[k].section));
};
// nivelul afișat: „Inclus automat” când secțiunea are doar ce cer alte secțiuni
const displayLevel = (sec, keys, chosen) => {
  const lv = sectionLevel(sec, keys);
  return lv !== "none" && !chosen.has(sec.key) ? "auto" : lv;
};
// cine a cerut o cheie adăugată automat (pentru „necesar pentru …”)
const requiredBy = (key, keys, reg) => [...keys].filter(k => k !== key && ((reg.keys[k] || {}).deps || []).includes(key));

function LevelBadge({ level, labels }) {
  const s = LEVEL_STYLE[level] || LEVEL_STYLE.none;
  return <span style={{ display: "inline-block", minWidth: 92, padding: "2px 8px", borderRadius: 4, fontSize: 11.5, fontWeight: 600,
    background: s.bg, color: s.color, whiteSpace: "nowrap", border: level === "none" ? "1px dashed #CBD5E1" : "none", textAlign: "center" }}>
    {level === "custom" ? "Personalizat" : level === "auto" ? "Inclus automat" : labels[level]}</span>;
}

export default function Access() {
  const { has } = useAuth();
  const loc = useLocation();
  const [tab, setTab] = useState("profiles");
  const [reg, setReg] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);   // { id?, kind, system_role, name, description, keys:Set, advanced }
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const [toDelete, setToDelete] = useState(null);
  const [checkUser, setCheckUser] = useState("");

  const toast = (text, type = "success") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 2500); };
  const err = (e) => setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" });

  const load = () => api.get("/access/profiles").then(r => setProfiles(r.data)).catch(err);
  useEffect(() => {
    api.get("/access/registry").then(r => setReg(r.data)).catch(err);
    load();
    // surse auxiliare: dacă una lipsește (fără drept), ecranul funcționează în continuare
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/mdm/org-units").then(r => setUnits(r.data)).catch(() => {});
    api.get("/config").then(r => setCategories(r.data?.personnel_categories || [])).catch(() => {});
    const uid = new URLSearchParams(loc.search).get("verifica");
    if (uid) { setTab("check"); setCheckUser(uid); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canManage = has("profiles.manage");
  const canSystem = has("profiles.system_manage");
  const canAssign = has("profiles.assign");
  const canCheck = has("users.access_check");

  const groups = useMemo(() => {
    if (!reg) return [];
    const out = [];
    reg.sections.forEach(s => {
      let g = out.find(x => x.title === s.group);
      if (!g) out.push(g = { title: s.group, sections: [] });
      g.sections.push(s);
    });
    return out;
  }, [reg]);

  if (!reg) return <div>Se încarcă...</div>;

  const openNew = () => setEditing({ kind: "custom", name: "", description: "", keys: new Set(), advanced: false });
  const openEdit = (p) => setEditing({ id: p.id, kind: p.kind, system_role: p.system_role, name: p.name, description: p.description || "",
    keys: new Set(p.keys), advanced: false });

  const save = async () => {
    if (!editing.name.trim()) return setMsg({ text: "Denumirea profilului este obligatorie", type: "error" });
    const body = { name: editing.name.trim(), description: editing.description, keys: [...closeKeys(editing.keys, reg)], levels: {} };
    try {
      if (editing.id) await api.put(`/access/profiles/${editing.id}`, body);
      else { const r = await api.post("/access/profiles", body); setEditing(null); await load(); toast("Profil creat. Acum îl poți aloca persoanelor."); return openEdit(r.data); }
      setEditing(null); load(); toast("Salvat");
    } catch (e) { err(e); }
  };
  const doDelete = async () => {
    try { await api.delete(`/access/profiles/${toDelete.id}`); setToDelete(null); load(); toast("Profil șters"); } catch (e) { err(e); }
  };

  const system = profiles.filter(p => p.kind === "system");
  const custom = profiles.filter(p => p.kind !== "system");

  return (
    <>
      <PageHeader title="Acces" subtitle="Ce poate face fiecare persoană: profilurile rolurilor (Admin, HR, Manager, Salariat) și profilurile de acces alocate suplimentar"
        actions={tab === "profiles" && canManage && <button className="btn btn-primary" onClick={openNew} data-testid="add-profile">+ Profil nou</button>} />
      <Toast msg={msg.text} type={msg.type} />

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {[["profiles", "Profiluri"], ["who", "Cine are acces"], ...(canCheck ? [["check", "Verifică o persoană"]] : [])].map(([k, l]) => (
          <button key={k} className={`btn ${tab === k ? "btn-primary" : "btn-secondary"}`} onClick={() => setTab(k)} data-testid={`access-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "profiles" && (
        <div className="card" style={{ overflowX: "auto" }}>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10, lineHeight: 1.5 }}>
            Nivelurile sunt cumulative: <b>Vizualizare</b> ⊂ <b>Operare</b> ⊂ <b>Administrare</b>. „Personalizat” = drepturi alese unul câte unul (Avansat);
            „Inclus automat” = doar ce cer alte secțiuni ca să funcționeze (de ex. lista structurilor pentru pontaj).
            Un profil de rol se aplică tuturor celor care au rolul; un profil personalizat, doar persoanelor cărora le este alocat.
          </div>
          <table className="tbl" data-testid="access-grid">
            <thead><tr>
              <th style={{ minWidth: 220 }}>Secțiune</th>
              {[...system, ...custom].map(p => (
                <th key={p.id} style={{ minWidth: 110, whiteSpace: "nowrap" }}>
                  <button className="btn btn-secondary" style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => openEdit(p)} data-testid={`profile-col-${p.name}`}
                    title={p.kind === "system" ? `Profilul rolului ${ROLE_LABELS[p.system_role]}` : `${p.member_count} alocări`}>
                    {p.name}{p.kind !== "system" && <span style={{ color: "#64748B", fontWeight: 400 }}> · {p.member_count}</span>}
                  </button>
                  <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 400, marginTop: 2 }}>{p.kind === "system" ? "rol" : "personalizat"}</div>
                </th>
              ))}
            </tr></thead>
            <tbody>{groups.map(g => (
              <React.Fragment key={g.title}>
                <tr><td colSpan={1 + profiles.length} style={{ fontWeight: 800, fontSize: 12, color: "var(--navy)", background: "#F8FAFC" }}>{g.title}</td></tr>
                {g.sections.map(s => (
                  <tr key={s.key}>
                    <td style={{ fontSize: 12.5 }}>{s.title}</td>
                    {[...system, ...custom].map(p => <td key={p.id}><LevelBadge level={displayLevel(s, new Set(p.keys), chosenSections(new Set(p.keys), reg))} labels={reg.levels} /></td>)}
                  </tr>
                ))}
              </React.Fragment>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab === "who" && <WhoTab reg={reg} groups={groups} />}
      {tab === "check" && <CheckTab reg={reg} users={users} uid={checkUser} setUid={setCheckUser} />}

      {editing && (
        <ProfileEditor reg={reg} groups={groups} editing={editing} setEditing={setEditing} onSave={save}
          readOnly={editing.kind === "system" ? !canSystem : (editing.id ? !canManage : false)}
          canAssign={canAssign} users={users} units={units} categories={categories} onMembersChanged={load} onError={err}
          onDelete={editing.kind !== "system" && editing.id && canManage ? () => setToDelete(editing) : null} />
      )}

      {toDelete && (
        <Modal title={`Șterge profilul „${toDelete.name}"?`} onClose={() => setToDelete(null)}>
          <div style={{ marginBottom: 16, fontSize: 13, color: "#334155" }}>Toate alocările acestui profil vor fi eliminate, iar persoanele pierd drepturile primite prin el. Acțiunea nu poate fi anulată.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-secondary" onClick={() => setToDelete(null)}>Anulează</button>
            <button className="btn btn-danger" onClick={() => { setEditing(null); doDelete(); }} data-testid="confirm-delete-profile">Șterge</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function ProfileEditor({ reg, groups, editing, setEditing, onSave, readOnly, canAssign, users, units, categories, onMembersChanged, onError, onDelete }) {
  const keys = closeKeys(editing.keys, reg);
  const chosen = chosenSections(keys, reg);
  const setLevel = (sec, lv) => {
    const next = new Set(editing.keys);
    levelKeys(sec, "admin").forEach(k => next.delete(k));
    levelKeys(sec, lv).forEach(k => next.add(k));
    setEditing({ ...editing, keys: next });
  };
  const toggleKey = (k) => {
    const next = new Set(editing.keys);
    if (next.has(k)) next.delete(k); else next.add(k);
    setEditing({ ...editing, keys: next });
  };
  const isSystem = editing.kind === "system";
  const role = editing.system_role;
  return (
    <Modal title={editing.id ? (isSystem ? `Profilul rolului ${ROLE_LABELS[role]}` : `Profil: ${editing.name}`) : "Profil nou"} onClose={() => setEditing(null)} width={920}>
      <div className="grid-2">
        <div><label className="label label-req">Denumire</label>
          <input className="input" value={editing.name} disabled={readOnly} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="profile-name" /></div>
        <div><label className="label">Descriere</label>
          <input className="input" value={editing.description} disabled={readOnly} onChange={e => setEditing({ ...editing, description: e.target.value })} /></div>
      </div>
      {isSystem && (
        <div style={{ marginTop: 10, fontSize: 12, padding: "8px 10px", borderRadius: 6, background: "var(--tint-50)", color: "var(--navy-3)" }}>
          Se aplică tuturor celor care au rolul <b>{ROLE_LABELS[role]}</b>; modificarea are efect imediat pentru toți.
          {readOnly && " Profilurile rolurilor le modifică doar Adminul."}
          {role === "admin" && " Adminul păstrează mereu Utilizatorii și Profilurile de acces, ca accesul să poată fi redat oricând."}
        </div>
      )}

      <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>Ce poate face</div>
        <label style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center", marginLeft: "auto", cursor: "pointer" }}>
          <input type="checkbox" checked={editing.advanced} onChange={e => setEditing({ ...editing, advanced: e.target.checked })} data-testid="profile-advanced" />
          Avansat (drept cu drept)
        </label>
      </div>
      <div style={{ display: "grid", gap: 14, maxHeight: 440, overflowY: "auto", marginTop: 8, paddingRight: 4 }}>
        {groups.map(g => (
          <div key={g.title}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "var(--navy)", borderBottom: "2px solid #E2E8F0", paddingBottom: 3, marginBottom: 6 }}>{g.title}</div>
            {g.sections.map(s => {
              const lv = displayLevel(s, keys, chosen);
              return (
                <div key={s.key} style={{ padding: "6px 8px", borderRadius: 6, background: ["none", "auto"].includes(lv) ? "transparent" : "#F8FAFC" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200, fontSize: 12.5 }}>{s.title}</div>
                    <Select className="select" value={lv} disabled={readOnly} onChange={e => !["custom", "auto"].includes(e.target.value) && setLevel(s, e.target.value)}
                      style={{ width: 170 }} data-testid={`level-${s.key}`}>
                      <option value="none">{reg.levels.none}</option>
                      {LEVELS.filter(l => s.levels[l]).map(l => <option key={l} value={l}>{reg.levels[l]}</option>)}
                      {lv === "custom" && <option value="custom">Personalizat</option>}
                      {lv === "auto" && <option value="auto">Inclus automat</option>}
                    </Select>
                  </div>
                  {editing.advanced && (
                    <div style={{ display: "grid", gap: 3, margin: "6px 0 2px 12px" }}>
                      {LEVELS.flatMap(l => s.levels[l] || []).map(k => {
                        const meta = reg.keys[k];
                        // bifat automat (și blocat) cât timp alt drept din profil are nevoie de el
                        const by = keys.has(k) ? requiredBy(k, keys, reg).map(x => reg.keys[x].label) : [];
                        const auto = by.length > 0;
                        const elig = meta.eligible_roles || [];
                        const ineligible = elig.length > 0 && (!isSystem || !elig.includes(role));
                        return (
                          <label key={k} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, cursor: readOnly || auto ? "default" : "pointer" }} data-testid={`key-${k}`}>
                            <input type="checkbox" checked={keys.has(k)} disabled={readOnly || auto} onChange={() => toggleKey(k)} />
                            <span>
                              {meta.label} <span style={{ color: "#94A3B8" }}>· {reg.levels[meta.level]}</span>
                              {auto && <span style={{ color: "#64748B" }}> — necesar pentru: {by.slice(0, 3).join("; ")}{by.length > 3 ? "…" : ""}</span>}
                              {ineligible && <span style={{ color: "#92400E" }}> — are efect doar pentru {elig.map(r => ROLE_LABELS[r]).join(" / ")}</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {editing.id && <Members profile={editing} canAssign={canAssign} users={users} units={units} categories={categories} onChanged={onMembersChanged} onError={onError} />}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        {onDelete && <button className="btn btn-danger" style={{ marginRight: "auto" }} onClick={onDelete} data-testid="delete-profile">Șterge profilul</button>}
        <button className="btn btn-secondary" onClick={() => setEditing(null)}>{readOnly ? "Închide" : "Anulează"}</button>
        {!readOnly && <button className="btn btn-primary" onClick={onSave} data-testid="save-profile">Salvează</button>}
      </div>
    </Modal>
  );
}

function Members({ profile, canAssign, users, units, categories, onChanged, onError }) {
  const [members, setMembers] = useState(null);
  const empty = { user_id: "", scope_org_unit_id: "", personnel_categories: [], valid_from: "", valid_to: "" };
  const [form, setForm] = useState(empty);
  const load = () => api.get(`/access/profiles/${profile.id}/members`).then(r => setMembers(r.data)).catch(() => setMembers([]));
  useEffect(() => { load(); }, [profile.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const unitName = (id) => (id ? (units.find(u => u.id === id)?.name || "?") : "Toată platforma");
  const today = new Date().toISOString().slice(0, 10);
  const add = async () => {
    if (!form.user_id) return;
    try {
      await api.post(`/access/profiles/${profile.id}/members`, { ...form, scope_org_unit_id: form.scope_org_unit_id || null,
        valid_from: form.valid_from || null, valid_to: form.valid_to || null });
      setForm(empty); load(); onChanged();
    } catch (e) { onError(e); }
  };
  const remove = async (m) => {
    try { await api.delete(`/access/profiles/${profile.id}/members/${m.id}`); load(); onChanged(); } catch (e) { onError(e); }
  };
  if (members === null) return null;
  if (profile.kind === "system") return (
    <div style={{ marginTop: 16, borderTop: "1px solid #E2E8F0", paddingTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>Persoane cu acest rol ({members.length})</div>
      <div style={{ fontSize: 12, color: "#475569", maxHeight: 120, overflowY: "auto" }}>{members.map(m => m.name).join(", ") || "—"}</div>
      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>Rolurile se schimbă din Utilizatori și Roluri.</div>
    </div>
  );
  return (
    <div style={{ marginTop: 16, borderTop: "1px solid #E2E8F0", paddingTop: 12 }} data-testid="profile-members">
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", marginBottom: 2 }}>Persoane cu acest profil ({members.length})</div>
      <div style={{ fontSize: 11, color: "#64748B", marginBottom: 8 }}>
        „Toată platforma” = fără restricție; altfel doar structura aleasă cu subunitățile ei. Categoriile de personal restrâng și mai mult (gol = toate).
        Perioada e opțională: în afara ei, profilul nu are efect.
      </div>
      <div style={{ display: "grid", gap: 4, marginBottom: 10 }}>
        {members.map(m => {
          const active = (!m.valid_from || m.valid_from <= today) && (!m.valid_to || m.valid_to >= today);
          return (
            <div key={m.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, flexWrap: "wrap" }} data-testid={`member-${m.username}`}>
              <b>{m.name}</b>
              <span style={{ color: "#64748B" }}>{unitName(m.scope_org_unit_id)}{(m.personnel_categories || []).length ? ` · ${m.personnel_categories.join(", ")}` : ""}</span>
              {(m.valid_from || m.valid_to) && <span style={{ color: active ? "#166534" : "#991B1B" }}>{m.valid_from || "…"} – {m.valid_to || "…"}{active ? "" : " (inactiv)"}</span>}
              {canAssign && <button className="btn btn-danger" style={{ padding: "1px 8px", fontSize: 11, marginLeft: "auto" }} onClick={() => remove(m)}>Retrage</button>}
            </div>
          );
        })}
        {members.length === 0 && <div style={{ fontSize: 12, color: "#94A3B8" }}>Nealocat încă.</div>}
      </div>
      {canAssign && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
            <Select className="select" value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} data-testid="member-user">
              <option value="">— alege persoana —</option>
              {users.filter(u => u.access_enabled !== false && u.active).map(u => <option key={u.id} value={u.id}>{u.last_name} {u.first_name} ({u.username})</option>)}
            </Select>
            <Select className="select" value={form.scope_org_unit_id} onChange={e => setForm({ ...form, scope_org_unit_id: e.target.value })} data-testid="member-scope">
              <option value="">Toată platforma</option>
              {units.filter(u => u.active).map(u => <option key={u.id} value={u.id}>N{u.level} · {u.name}</option>)}
            </Select>
            <MultiSelect options={categories} value={form.personnel_categories} onChange={v => setForm({ ...form, personnel_categories: v })}
              allLabel="Toate categoriile de personal" testId="member-categories" minWidth={200} />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: 12 }}>De la <input type="date" className="input" style={{ width: 150, display: "inline-block" }} value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} /></label>
            <label style={{ fontSize: 12 }}>Până la <input type="date" className="input" style={{ width: 150, display: "inline-block" }} value={form.valid_to} onChange={e => setForm({ ...form, valid_to: e.target.value })} /></label>
            <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={add} disabled={!form.user_id} data-testid="member-add">+ Alocă</button>
          </div>
          {form.user_id && <CoverageHint scopeIds={form.scope_org_unit_id ? [form.scope_org_unit_id] : []} categories={form.personnel_categories} />}
        </>
      )}
    </div>
  );
}

function WhoTab({ reg, groups }) {
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!key) return setData(null);
    api.get("/access/who", { params: { key } }).then(r => setData(r.data)).catch(() => setData(null));
  }, [key]);
  const src = (s) => s.type === "role" ? `rol ${ROLE_LABELS[s.role]}` :
    `profil „${s.profile}” · ${s.scope}${(s.personnel_categories || []).length ? ` · ${s.personnel_categories.join(", ")}` : ""}`;
  return (
    <div className="card">
      <label className="label">Dreptul</label>
      <Select className="select" value={key} onChange={e => setKey(e.target.value)} data-testid="who-key">
        <option value="">— alege un drept —</option>
        {groups.flatMap(g => g.sections.flatMap(s => LEVELS.flatMap(l => s.levels[l] || []).map(k =>
          <option key={k} value={k}>{s.title} — {reg.keys[k].label}</option>)))}
      </Select>
      {data && (
        <div style={{ marginTop: 12 }} data-testid="who-result">
          {data.eligible_roles.length > 0 && <div style={{ fontSize: 12, color: "#92400E", marginBottom: 6 }}>Are efect doar pentru {data.eligible_roles.map(r => ROLE_LABELS[r]).join(" / ")}.</div>}
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{data.users.length} persoane</div>
          <table className="tbl"><thead><tr><th>Persoana</th><th>De unde are dreptul</th></tr></thead>
            <tbody>{data.users.map(u => <tr key={u.user_id}><td>{u.name} <span style={{ color: "#94A3B8" }}>({u.username})</span></td>
              <td style={{ fontSize: 12 }}>{u.sources.map(src).join("; ")}</td></tr>)}</tbody></table>
          {data.ineligible.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#92400E" }}>
              Fără efect (profilul le dă dreptul, dar nu au rolul cerut): {data.ineligible.map(u => u.name).join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const DIAG_KEYS = [["timeentries.view_all", "Pontaj Proiecte — vede pontajul altora"], ["timeentries.edit_others", "Pontaj Proiecte — completează pontajul altora"],
                   ["documents.view_all", "Dosar Electronic — vede dosarele altora"], ["employments.view", "Rapoarte de muncă — vede lista"]];

function CheckTab({ reg, users, uid, setUid }) {
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null); setError("");
    if (!uid) return;
    api.get(`/access/check/${uid}`, { params: key ? { key } : {} }).then(r => setData(r.data)).catch(e => setError(e?.response?.data?.detail || "Eroare"));
  }, [uid, key]);
  const titles = Object.fromEntries(reg.sections.map(s => [s.key, s]));
  return (
    <div className="card">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
        <div><label className="label">Persoana</label>
          <Select className="select" value={uid} onChange={e => setUid(e.target.value)} data-testid="check-user">
            <option value="">— alege persoana —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.last_name} {u.first_name} ({u.username})</option>)}
          </Select></div>
        <div><label className="label">De ce nu vede anumite rapoarte de muncă? (opțional)</label>
          <Select className="select" value={key} onChange={e => setKey(e.target.value)} data-testid="check-key">
            <option value="">—</option>
            {DIAG_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select></div>
      </div>
      {error && <div style={{ color: "#991B1B", marginTop: 10 }}>{error}</div>}
      {data && (
        <div style={{ marginTop: 12, fontSize: 13, color: "#334155" }} data-testid="check-result">
          <div>Roluri: <b>{data.roles.map(r => ROLE_LABELS[r] || r).join(", ") || "—"}</b>{data.access_enabled === false && " · fără acces în platformă"}{data.active === false && " · inactiv"}</div>
          <table className="tbl" style={{ marginTop: 8 }}><thead><tr><th>Secțiune</th><th>Nivel</th><th>Din</th></tr></thead>
            <tbody>{Object.entries(data.sections).filter(([, v]) => v.level !== "none").map(([k, v]) => (
              <tr key={k}><td style={{ fontSize: 12.5 }}>{titles[k]?.title}</td><td><LevelBadge level={v.level} labels={reg.levels} /></td>
                <td style={{ fontSize: 12, color: "#64748B" }}>{v.sources.join(", ")}</td></tr>))}</tbody></table>
          {data.blocked.length > 0 && <div style={{ marginTop: 8, fontSize: 12, color: "#92400E" }}>
            Fără efect (cer alt rol): {data.blocked.map(b => `${b.label} (doar ${b.eligible_roles.map(r => ROLE_LABELS[r]).join(" / ")})`).join("; ")}</div>}
          {data.coverage && <Coverage d={data.coverage} />}
        </div>
      )}
    </div>
  );
}

function Coverage({ d }) {
  return (
    <div style={{ marginTop: 12, lineHeight: 1.6 }} data-testid="diag-result">
      <div>Rolul permite „{d.key_label}”: <b>{d.base_role_allows ? "da" : "nu"}</b></div>
      <div style={{ marginTop: 6, fontWeight: 700 }}>Profiluri alocate ({d.assignments.length}) — {d.assignments_with_key} includ acest drept</div>
      {d.assignments.map((a, i) => (
        <div key={i} style={{ padding: "6px 10px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, margin: "4px 0" }}>
          <b>{a.profile}</b> · {a.scope}{a.personnel_categories.length ? ` · categorii: ${a.personnel_categories.join(", ")}` : " · toate categoriile"}
          <div style={{ color: a.has_key ? "#166534" : "#991B1B" }}>{a.has_key ? "Include dreptul verificat." : "NU include dreptul verificat."}</div>
          {a.structures_total !== undefined && <div>Structuri sub alocare: {a.structures_total}, accesibile: {a.structures_reachable}{a.structures_blocked.length > 0 && <span style={{ color: "#991B1B" }}> — blocate de o structură dezactivată: {a.structures_blocked.join(", ")}</span>}</div>}
          {a.scope_active === false && <div style={{ color: "#991B1B" }}>Structura alocată este dezactivată.</div>}
        </div>))}
      <div style={{ marginTop: 6 }}>Rapoarte de muncă acoperite de alocări: <b>{d.employments_covered}</b></div>
      {Object.keys(d.reasons).length > 0 && <div>Neacoperite: {Object.entries(d.reasons).map(([k, v]) => `${v} — ${k}`).join(" · ")}</div>}
      {d.examples.length > 0 && <div style={{ marginTop: 6 }}>Exemple: {d.examples.map((x, i) => <div key={i} style={{ fontSize: 12 }}>{x.name} ({x.marca}) · {x.structure} · {x.category} — {x.reason}</div>)}</div>}
    </div>
  );
}
